import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import { RoundingUnitKind, UnitName } from "@sinch/core/entities/serverEnums";
import type { UnitName as UnitNameType } from "@sinch/core/entities/serverEnums.types";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { RoundingSelectInput } from "@sinch/components/application/RateRounding/RoundingSelectInput";

export type AnyUnitName = UnitNameType | UnitName | null | undefined;

/**
 * Kind of rounding a unit name is rounded by, null for units that are never rounded
 */
export const roundingKindOfUnit = (unitName: AnyUnitName): RoundingUnitKind | null => {
  if (unitName === UnitName.Hour) {
    return RoundingUnitKind.Time;
  }
  if (unitName === UnitName.Kilometer || unitName === UnitName.Mile) {
    return RoundingUnitKind.Distance;
  }
  return null;
};

/**
 * Whether a rule-level rounding survives a change of the rule's unit: only while the new unit is rounded by
 * the same kind, so a distance rounding never lingers on travel hours and no rounding at all lingers on a
 * percentage or pieces unit, where nothing would show it
 */
export const keepsRoundingOnUnitChange = (previous: AnyUnitName, next: AnyUnitName): boolean => {
  const nextKind = roundingKindOfUnit(next);
  return nextKind !== null && roundingKindOfUnit(previous) === nextKind;
};

/**
 * Per-rule rounding override inside the unit dialog
 */
export const RoundingInput: FC<{
  value: string | null;
  onChange: (roundingId: string | null) => void;
  unitName: AnyUnitName;
}> = ({ value, onChange, unitName }) => {
  const { t } = useTranslation();
  const kind = roundingKindOfUnit(unitName);
  if (!kind) {
    return null;
  }
  return (
    <>
      <FormLabel sx={{ display: "flex", alignItems: "left", mt: 1 }}>{t("RateBuilder.rounding")}</FormLabel>
      <RoundingSelectInput
        data-cy="IntervalSelect-rounding-select"
        // the unit dialog is a click-away popover: a menu rendered outside of it would close the dialog on pick
        disablePortal
        onChange={onChange}
        unitKind={kind}
        value={value}
      />
      <FormHelperText sx={{ pl: 1 }}>{t("RateBuilder.roundingRuleDescription")}</FormHelperText>
    </>
  );
};
